/**
 * Subscription types — Chargebee (web) + RevenueCat IAP (mobile).
 * Simple model: 14-day free trial → single paid plan.
 *
 * Billing is split by platform and discriminated by `subscriptionSource`:
 *   - chargebee: web subscriptions
 *   - apple_iap / google_iap: mobile in-app purchases (via RevenueCat)
 * DynamoDB is the single source of truth; each source writes its own SUB# record
 * and precedence is resolved at read time (Apple wins among active sources).
 *
 * UPDATED for Chargebee Product Catalog 2.0:
 *   - Plan IDs are now "item price IDs" (e.g. TableTryb-USD-Monthly)
 *   - Prices updated: monthly $3.99, annual $39.99
 */

export type SubscriptionStatus =
  | 'in_trial'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'expired'
  | 'none'; // No subscription yet (just signed up, hasn't created household)

/**
 * Which billing system owns a subscription record.
 *   - chargebee:  web subscriptions (Chargebee)
 *   - apple_iap:  iOS in-app purchase (via RevenueCat)
 *   - google_iap: Android in-app purchase (via RevenueCat) — post-launch
 * Precedence among concurrently-active sources is Apple (resolved at read time in get-status).
 */
export type SubscriptionSource = 'chargebee' | 'apple_iap' | 'google_iap';

export interface Subscription {
  householdId: string;
  /** Which billing system owns this record — discriminator for precedence + manage-subscription routing. */
  subscriptionSource: SubscriptionSource;
  /** Chargebee subscription ID (web). Optional: absent on IAP records. */
  chargebeeSubscriptionId?: string;
  /** Chargebee customer ID (web). Optional: absent on IAP records. */
  chargebeeCustomerId?: string;
  status: SubscriptionStatus;
  /** Chargebee item price ID, or the store product ID for IAP records. */
  planId: string;
  /** Storefront the IAP came from (mobile, via RevenueCat). Absent on Chargebee records. */
  store?: 'app_store' | 'play_store';
  /** Apple/Google original transaction ID — stable IAP subscription identifier. */
  originalTransactionId?: string;
  trialStartedAt?: string;
  trialEndsAt?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelledAt?: string;
  updatedAt: string;
}

export interface SubscriptionStatusResponse {
  status: SubscriptionStatus;
  planId: string;
  trialDaysRemaining?: number;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  /** URL to Chargebee customer portal for billing management */
  portalUrl?: string;
  /**
   * Which billing system owns the effective subscription, for manage-subscription routing.
   * Apple/Google IAP → deep-link to the OS subscription settings; Chargebee → portalUrl.
   * Optional: absent when status is 'none' or a local (pre-subscription) trial.
   */
  subscriptionSource?: SubscriptionSource;
  /**
   * True when BOTH a Chargebee and an IAP subscription are simultaneously access-active
   * for the same household — surfaces possible double-billing so the web profile page
   * can warn the user. Absent (not false) when there is no conflict.
   */
  hasConflictingSources?: boolean;
}

export interface CreateCheckoutRequest {
  householdId: string;
  planId: string;
  /** Redirect URL after checkout completes */
  redirectUrl: string;
  /** Optional referral code from the referrer */
  referralCode?: string;
}

export interface CreateCheckoutResponse {
  checkoutUrl: string;
}

/**
 * Chargebee webhook event types we handle.
 * See: https://apidocs.chargebee.com/docs/api/events
 */
export type ChargebeeEventType =
  | 'subscription_created'
  | 'subscription_started'
  | 'subscription_activated'
  | 'subscription_renewed'
  | 'subscription_cancelled'
  | 'subscription_reactivated'
  | 'subscription_changed'
  | 'subscription_trial_end_reminder'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'payment_refunded';

/**
 * Plans configuration — Chargebee PC 2.0 item price IDs.
 * These must match the item price IDs configured in the Chargebee dashboard.
 */
export const PLANS = {
  TABLETRYB_MONTHLY: {
    id: 'TableTryb-USD-Monthly',
    name: 'TableTryb Monthly',
    price: 399, // cents — $3.99/mo
    currency: 'USD',
    interval: 'month' as const,
    trialDays: 14,
  },
  TABLETRYB_ANNUAL: {
    id: 'TableTryb-USD-Yearly',
    name: 'TableTryb Annual',
    price: 3999, // cents — $39.99/yr
    currency: 'USD',
    interval: 'year' as const,
    trialDays: 14,
  },
} as const;

/**
 * Whether a subscription status grants the household access to paid features.
 * Used by:
 *   - `get-status` precedence (Apple wins among access-active sources)
 *   - `create-checkout` guard (refuse Chargebee checkout when an IAP record is access-active)
 *   - web frontend (show/hide trial + subscribe banners)
 *
 * `past_due` counts as active because it is the Apple billing grace period — the user
 * still has entitlement during retry. Defined once here so the read precedence and the
 * checkout guard cannot disagree.
 */
export function isAccessActive(status: SubscriptionStatus): boolean {
  return status === 'in_trial' || status === 'active' || status === 'past_due';
}